/**
 * ShiftHandoffDialog — small modal a worker on a rotation uses to leave a
 * note for the incoming shift. Body is free-form; for_date defaults to the
 * server-side "tomorrow in user's timezone" if omitted.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ShiftHandoffDialog({ open, onOpenChange, rotationId, rotationName, onSaved }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!body.trim()) {
      toast.error('Add a quick note for the next shift');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/shift-handoffs`,
        { rotation_id: rotationId, body: body.trim() },
        { headers: authHeaders() },
      );
      toast.success('Handoff posted — next shift will see it on their dashboard.');
      setBody('');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not post handoff');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="shift-handoff-dialog" aria-describedby="handoff-dialog-desc">
        <DialogHeader>
          <DialogTitle>Leave handoff for next shift</DialogTitle>
        </DialogHeader>
        <p id="handoff-dialog-desc" className="sr-only">
          Leave a free-form note for the worker picking up your shift tomorrow.
        </p>
        <div className="space-y-2">
          {rotationName && (
            <p className="text-xs text-slate-500">
              Rotation: <span className="font-medium">{rotationName}</span>
            </p>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Equipment status, partial work, callbacks needed, hazards to know about..."
            rows={6}
            data-testid="handoff-body"
            autoFocus
          />
          <p className="text-xs text-slate-500">
            Sent to whoever picks up your shift tomorrow. They'll get an in-app card and SMS if opted in.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !body.trim()} data-testid="handoff-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Post Handoff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
